import React, {useEffect, useRef, useState} from 'react';
import {Badge, Block, Card, Divider, Label, Space, Text, Title} from '@mc/ui-v4';
import './MeasurementCard.scss';
import {FeaturePointer, GeometryMeasurementItem, MeasurementCardData} from '@mc/react-mapio';

/**
 * Aktuální výběr v měření (řízený z mapy/karty přes klient).
 * edgeIndex = číslo → vybraná hrana; edgeIndex = null → vybraná celá (sub-)část.
 */
export type HighlightedEdge = {partIndex: number; edgeIndex: number | null};

type Props = {
    measurementData: MeasurementCardData | null;
    /** Ukazatele na měřené (sub-)geometrie (v pořadí shodném s measurementData.geometries) */
    featurePointers?: FeaturePointer[];
    /** Callback pro zvýraznění geometrie/hrany v mapě (hover=true dočasně, hover=false trvalý výběr) */
    onHighlightFeature?: (pointer: FeaturePointer | null, hover: boolean) => void;
    highlightedEdge?: HighlightedEdge | null;
};

const FIXED_DIGITS = 2;
const MAX_SEGMENTS_COLLAPSED = 12;

/**
 * Formátuje číslo s českou lokalizací (čárka místo tečky)
 */
const formatNumber = (value: number | undefined): string => value?.toFixed(FIXED_DIGITS).replace('.', ',') ?? '---';

/**
 * Formátuje vzdálenost - pro malé hodnoty zobrazí v cm, pro větší v m
 */
const formatDistance = (meters: number | undefined): string => {
    if (meters === undefined) return '---';
    if (meters < 1) {
        return `${formatNumber(meters * 100)} cm`;
    }
    return `${formatNumber(meters)} m`;
};

/** Vytvoří ukazatel na konkrétní hranu (geometrie + index hrany) pro zvýraznění v mapě. */
const edgePointer = (pointer: FeaturePointer | undefined, edgeIndex: number): FeaturePointer | null =>
    pointer ? ({...pointer, edgeIndex} as FeaturePointer) : null;

type GeometryItemProps = {
    item: GeometryMeasurementItem;
    index: number;
    /** Více (sub-)geometrií → číslujeme řádky a povolujeme zvýraznění celé části klikem na hlavičku. */
    isMulti: boolean;
    /** Je vybraná (kliknutím) celá tato část? Řídí zvýraznění řádku. */
    isPartSelected: boolean;
    pointer?: FeaturePointer;
    onHighlight: (pointer: FeaturePointer | null, hover: boolean) => void;
    /** Index hrany, kterou zvýraznit (přišlo kliknutím na hranu/Badge), jinak null. */
    highlightedEdgeIndex?: number | null;
};

const GeometryMeasurementRow: React.FC<GeometryItemProps> = ({item, index, isMulti, isPartSelected, pointer, onHighlight, highlightedEdgeIndex}) => {
    const [expanded, setExpanded] = useState(false);
    const activeSegmentRef = useRef<HTMLButtonElement | null>(null);

    const {area, perimeter, segmentDistances, geometryType} = item;
    const isPolygon = geometryType === 'Polygon' || geometryType === 'MultiPolygon';

    const hasMore = segmentDistances.length > MAX_SEGMENTS_COLLAPSED;

    // Pokud má zvýrazněná hrana index ve skryté části, rozbalíme seznam a odrolujeme na ni.
    useEffect(() => {
        if (highlightedEdgeIndex == null) return;
        if (highlightedEdgeIndex >= MAX_SEGMENTS_COLLAPSED) setExpanded(true);
        activeSegmentRef.current?.scrollIntoView({block: 'nearest', inline: 'nearest'});
    }, [highlightedEdgeIndex]);

    const visibleSegments = hasMore && !expanded ? segmentDistances.slice(0, MAX_SEGMENTS_COLLAPSED) : segmentDistances;

    // Zvýraznění celé části má smysl jen u multigeometrií – přepíná se kliknutím na hlavičku
    // (toggle řeší klient). Nepřekrývá tak při hoveru zvýraznění vybrané hrany.
    const togglePart = isMulti && pointer ? () => onHighlight(pointer, false) : undefined;

    return (
        <Block bodyClassName="p-0" className={`measurement-row ${isPartSelected ? 'measurement-row--highlighted' : ''}`}>
            <div className="p-2">
                <div role={togglePart ? 'button' : undefined} onClick={togglePart}>
                    <Card
                        headerClassName="measurement-card-header py-1"
                        title={
                            <div>
                                {isPolygon && area !== undefined ? (
                                    <Space className="d-flex justify-content-between">
                                        <Title className="measurement-label" type={'block'} intent={'inherit'}>
                                            {isMulti ? `Plocha ${index + 1}` : 'Plocha'}
                                        </Title>
                                        <Title className="measurement-label" type={'block'} intent={'inherit'}>
                                            {formatNumber(area)}{' '}
                                            <Text>
                                                m<sup>2</sup>
                                            </Text>
                                        </Title>
                                    </Space>
                                ) : (
                                    <Space className="d-flex justify-content-between">
                                        <Title className="measurement-label" type={'block'} intent={'inherit'}>
                                            {isMulti ? `Délka ${index + 1}` : 'Délka'}
                                        </Title>
                                        <Title className="measurement-label" type={'block'} intent={'inherit'}>
                                            {formatNumber(perimeter)} <Text>m</Text>
                                        </Title>
                                    </Space>
                                )}
                            </div>
                        }
                    >
                        <Space className="d-flex justify-content-between py-1 px-2">
                            <Title type={'block'} className="measurement-label" intent={'inherit'}>
                                Celková délka
                            </Title>
                            <Title type={'block'} className="measurement-label" intent={'inherit'}>
                                {formatDistance(perimeter)}
                            </Title>
                        </Space>
                    </Card>
                </div>

                {/* Délky hran */}
                {segmentDistances && segmentDistances.length > 0 && (
                    <div className={'measurement-card-distances pt-2'}>
                        <Label className={'measurement-distances-label'}>Délky hran</Label>
                        <Space className={'measurement-tags'} wrap>
                            {visibleSegments.map((distance, idx) => {
                                const isActive = highlightedEdgeIndex === idx;
                                return (
                                    <button
                                        type="button"
                                        key={`segment-${index}-${idx}-${distance}`}
                                        ref={isActive ? activeSegmentRef : undefined}
                                        className={`measurement-segment ${isActive ? 'measurement-segment--active' : ''}`}
                                        // Klik na Badge → trvalé oranžové zvýraznění hrany v mapě (hover=false).
                                        onClick={() => onHighlight(edgePointer(pointer, idx), false)}
                                    >
                                        <Badge intent={isActive ? 'primary' : 'subsection'}>{formatDistance(distance)}</Badge>
                                    </button>
                                );
                            })}
                        </Space>
                        {hasMore && (
                            <button className="measurement-show-more" onClick={() => setExpanded(e => !e)}>
                                {expanded ? 'Zobrazit méně' : `Zobrazit více (${segmentDistances.length - MAX_SEGMENTS_COLLAPSED} dalších)`}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </Block>
    );
};

export const MeasurementCard: React.FC<Props> = ({measurementData, featurePointers, onHighlightFeature, highlightedEdge}) => {
    if (!measurementData) return null;

    const {geometries} = measurementData;
    if (!geometries || geometries.length === 0) return null;

    // Filtrujeme body
    const validGeometries = geometries.filter(g => g.geometryType !== 'Point');
    if (validGeometries.length === 0) return null;

    const isMulti = validGeometries.length > 1;
    const handleHighlight = onHighlightFeature ?? (() => {});

    return (
        <Card className={'measurement-card drop-shadow'} mode={'section'}>
            <div className="measurement-geometries-scroll">
                {validGeometries.map((item, idx) => {
                    const pointer = featurePointers?.[idx];
                    const isThisPart = !!highlightedEdge && pointer?.partIndex === highlightedEdge.partIndex;
                    // edgeIndex číslo → zvýrazněná hrana; edgeIndex null → vybraná celá část.
                    const highlightedEdgeIndex = isThisPart && highlightedEdge!.edgeIndex !== null ? highlightedEdge!.edgeIndex : null;
                    const isPartSelected = isThisPart && highlightedEdge!.edgeIndex === null;
                    return (
                        <React.Fragment key={`geometry-${item.geometryType}-${item.perimeter?.toFixed(4)}-${idx}`}>
                            <GeometryMeasurementRow
                                item={item}
                                index={idx}
                                isMulti={isMulti}
                                isPartSelected={isPartSelected}
                                pointer={pointer}
                                onHighlight={handleHighlight}
                                highlightedEdgeIndex={highlightedEdgeIndex}
                            />
                            <Divider horizontal />
                        </React.Fragment>
                    );
                })}
            </div>
        </Card>
    );
};
